import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_URL } from "../config/contracts";

interface MatchOdds {
  id: number;
  homeScore: number;
  awayScore: number;
  odds: string;
  reverseOdds: string;
}

interface Match {
  id: number;
  apiMatchId: number;
  homeTeam: string;
  awayTeam: string;
  league: string;
  kickoffTime: string;
  state: string;
  odds: MatchOdds[];
}

interface MatchResponse {
  matches: Match[];
  total: number;
  page: number;
  limit: number;
}

export function MatchesPage() {
  const [data, setData] = useState<MatchResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);

  useEffect(() => {
    fetchMatches();
  }, []);

  async function fetchMatches() {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/matches`);
      if (!res.ok) throw new Error("Failed to fetch matches");
      const json = await res.json();
      setData(json);
    } catch {
      // API unreachable or error — show empty state instead of error
      setData({ matches: [], total: 0, page: 1, limit: 20 });
    } finally {
      setLoading(false);
    }
  }

  function formatKickoff(dateStr: string) {
    const d = new Date(dateStr);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function timeUntilKickoff(dateStr: string) {
    const diff = new Date(dateStr).getTime() - Date.now();
    if (diff <= 0) return "Started";
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  }

  if (loading) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold mb-6">Upcoming Matches</h2>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-800 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }


  const matches = data?.matches || [];

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Upcoming Matches</h2>
        <Link to="/" className="text-blue-400 hover:text-blue-300 text-sm">
          Home
        </Link>
      </div>

      {matches.length === 0 ? (
        <div className="text-center py-12 bg-gray-900 rounded-lg">
          <p className="text-gray-400 text-lg">No upcoming matches available</p>
          <p className="text-gray-500 mt-2">Check back later for new fixtures</p>
        </div>
      ) : (
        <div className="space-y-4">
          {matches.map((match) => (
            <div
              key={match.id}
              className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden"
            >
              {/* Match header */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">
                    {match.league}
                  </span>
                  <span className="text-xs text-yellow-400">
                    {timeUntilKickoff(match.kickoffTime)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex-1 text-center">
                    <p className="text-lg font-semibold">{match.homeTeam}</p>
                  </div>
                  <div className="px-4">
                    <p className="text-xs text-gray-500">
                      {formatKickoff(match.kickoffTime)}
                    </p>
                    <p className="text-center text-gray-600 font-bold">VS</p>
                  </div>
                  <div className="flex-1 text-center">
                    <p className="text-lg font-semibold">{match.awayTeam}</p>
                  </div>
                </div>
              </div>

              {/* Odds preview + expand button */}
              <div className="border-t border-gray-800 px-4 py-3">
                {match.odds.length > 0 ? (
                  <div>
                    <button
                      onClick={() =>
                        setSelectedMatch(
                          selectedMatch?.id === match.id ? null : match,
                        )
                      }
                      className="w-full flex items-center justify-between text-sm"
                    >
                      <span className="text-gray-400">
                        {match.odds.length} score lines available
                      </span>
                      <span className="text-blue-400 hover:text-blue-300">
                        {selectedMatch?.id === match.id ? "Hide Odds" : "View Odds & Bet"}
                      </span>
                    </button>

                    {selectedMatch?.id === match.id && (
                      <OddsTable match={match} />
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Odds not yet available</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {data && data.total > data.limit && (
        <div className="mt-6 text-center text-gray-500 text-sm">
          Showing {matches.length} of {data.total} matches
        </div>
      )}
    </div>
  );
}

function OddsTable({ match }: { match: Match }) {
  const [selectedScore, setSelectedScore] = useState<MatchOdds | null>(null);
  const [betType, setBetType] = useState<"CORRECT_SCORE" | "REVERSE_PARLAY">(
    "REVERSE_PARLAY",
  );
  const [betAmount, setBetAmount] = useState("");

  const sortedOdds = [...match.odds].sort((a, b) => {
    if (a.homeScore !== b.homeScore) return a.homeScore - b.homeScore;
    return a.awayScore - b.awayScore;
  });

  function getDisplayOdds(o: MatchOdds) {
    return betType === "CORRECT_SCORE"
      ? parseFloat(o.odds).toFixed(2)
      : parseFloat(o.reverseOdds).toFixed(4);
  }

  function getPotentialPayout() {
    if (!selectedScore || !betAmount || parseFloat(betAmount) <= 0) return null;
    const odds =
      betType === "CORRECT_SCORE"
        ? parseFloat(selectedScore.odds)
        : parseFloat(selectedScore.reverseOdds);
    return (parseFloat(betAmount) * odds).toFixed(2);
  }

  return (
    <div className="mt-4 space-y-4">
      {/* Bet type selector */}
      <div className="flex gap-2">
        <button
          onClick={() => setBetType("REVERSE_PARLAY")}
          className={`flex-1 py-2 rounded text-sm font-medium transition ${
            betType === "REVERSE_PARLAY"
              ? "bg-green-600 text-white"
              : "bg-gray-800 text-gray-400 hover:bg-gray-700"
          }`}
        >
          Reverse Parlay
        </button>
        <button
          onClick={() => setBetType("CORRECT_SCORE")}
          className={`flex-1 py-2 rounded text-sm font-medium transition ${
            betType === "CORRECT_SCORE"
              ? "bg-purple-600 text-white"
              : "bg-gray-800 text-gray-400 hover:bg-gray-700"
          }`}
        >
          Correct Score
        </button>
      </div>

      {/* Odds grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
        {sortedOdds.map((o) => (
          <button
            key={`${o.homeScore}-${o.awayScore}`}
            onClick={() => setSelectedScore(o)}
            className={`p-2 rounded text-center text-sm border transition ${
              selectedScore?.homeScore === o.homeScore &&
              selectedScore?.awayScore === o.awayScore
                ? "border-blue-500 bg-blue-900/30"
                : "border-gray-700 bg-gray-800/50 hover:border-gray-600"
            }`}
          >
            <div className="font-medium">
              {o.homeScore}-{o.awayScore}
            </div>
            <div className="text-xs text-yellow-400 mt-1">
              {getDisplayOdds(o)}x
            </div>
          </button>
        ))}
      </div>

      {/* Bet form */}
      {selectedScore && (
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="text-sm text-gray-400">
                {betType === "REVERSE_PARLAY" ? "Bet NOT" : "Bet"}{" "}
              </span>
              <span className="font-semibold">
                {selectedScore.homeScore}-{selectedScore.awayScore}
              </span>
            </div>
            <span className="text-yellow-400 font-mono">
              {getDisplayOdds(selectedScore)}x
            </span>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-gray-500 mb-1 block">
                Amount (USDT)
              </label>
              <input
                type="number"
                min="1"
                step="1"
                placeholder="100"
                value={betAmount}
                onChange={(e) => setBetAmount(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500 mb-1 block">
                Potential Payout
              </label>
              <div className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-green-400 font-mono">
                {getPotentialPayout() ? `${getPotentialPayout()} USDT` : "\u2014"}
              </div>
            </div>
          </div>

          <button
            disabled={!betAmount || parseFloat(betAmount) <= 0}
            className="mt-3 w-full py-3 bg-blue-600 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Place {betType === "REVERSE_PARLAY" ? "Reverse Parlay" : "Correct Score"} Bet
          </button>

          <p className="text-xs text-gray-500 mt-2 text-center">
            Wallet transaction required. 3-minute cancellation window applies.
          </p>
        </div>
      )}
    </div>
  );
}
